import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Drives the "Merge & Remove Duplicate Project" wizard and the revert flow.
// Wraps both modals plus the Administration row so all data-action targets
// resolve to this controller. Subscribes to ProjectMergeChannel to stream
// progress into a sticky toast and reload on completion.
export default class extends Controller {
  static targets = [
    "searchInput", "searchResults", "searchEmpty",
    "step1", "step2", "step3",
    "stepLabel1", "stepLabel2", "stepLabel3",
    "destinationName", "sourceName",
    "comparisonBody",
    "typedName", "typedNameHint", "password",
    "backBtn", "continueBtn", "confirmBtn", "confirmError",
    "revertPassword", "revertMergeId", "revertError"
  ]

  static values = {
    projectId: Number,
    projectName: String,
    csrfToken: String
  }

  connect() {
    this._currentStep = 1
    this._selectedSource = null
    this._modules = ["reports", "action_items", "finances", "photos", "documents"]
    this._searchTimer = null
  }

  disconnect() {
    if (this._subscription) this._subscription.unsubscribe()
  }

  // ── Wizard open / step transitions ──────────────────────────────────────

  openWizard() {
    this._resetWizard()
    this._openModal("merge-duplicate")
  }

  openRevert(event) {
    const mergeId = event.currentTarget.dataset.mergeId
    if (!mergeId) return
    if (this.hasRevertMergeIdTarget) this.revertMergeIdTarget.value = mergeId
    if (this.hasRevertPasswordTarget) this.revertPasswordTarget.value = ""
    if (this.hasRevertErrorTarget) this.revertErrorTarget.classList.add("hidden")
    this._openModal("revert-merge")
  }

  next() {
    if (this._currentStep === 1) this._goTo(2)
    else if (this._currentStep === 2) this._goTo(3)
  }

  back() {
    if (this._currentStep === 2) this._goTo(1)
    else if (this._currentStep === 3) this._goTo(2)
  }

  // ── Step 1: search ─────────────────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimer)
    const q = this.searchInputTarget.value.trim()
    this._searchTimer = setTimeout(() => this._fetchEligible(q), 250)
  }

  _fetchEligible(q) {
    const url = `/projects/${this.projectIdValue}/merges/eligible_projects?q=${encodeURIComponent(q)}`
    fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then(r => r.json())
      .then(results => this._renderResults(results || []))
      .catch(() => this._renderResults([]))
  }

  _renderResults(results) {
    this.searchResultsTarget.innerHTML = ""
    if (results.length === 0) {
      if (this.hasSearchEmptyTarget) this.searchEmptyTarget.classList.remove("hidden")
      return
    }
    if (this.hasSearchEmptyTarget) this.searchEmptyTarget.classList.add("hidden")
    results.forEach(r => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 flex items-center justify-between"
      row.innerHTML = `<span>${this._escape(r.name)}</span><span class="text-text-muted text-[12px]">${this._escape(r.number || "")}</span>`
      row.addEventListener("click", () => this._selectSource(r))
      this.searchResultsTarget.appendChild(row)
    })
  }

  _selectSource(project) {
    this._selectedSource = project
    this._fetchPreview(project.id)
  }

  // ── Step 2: comparison ─────────────────────────────────────────────────

  _fetchPreview(sourceId) {
    const url = `/projects/${this.projectIdValue}/merges/preview?source_id=${sourceId}`
    fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then(r => r.json())
      .then(data => {
        this._renderComparison(data)
        this._goTo(2)
      })
      .catch(() => this._showToast("Couldn't load comparison", "error"))
  }

  _renderComparison(data) {
    if (!data || !data.source || !data.destination) return
    this.destinationNameTarget.textContent = data.destination.name
    this.sourceNameTarget.textContent = data.source.name
    this.typedNameHintTarget.textContent = data.source.name

    const types = Object.keys(data.source.counts)
    this.comparisonBodyTarget.innerHTML = ""
    types.forEach(type => {
      const moduleKey = this._moduleKeyForType(type)
      const checkboxId = `merge-mod-${moduleKey}`
      const srcVal = data.source.counts[type]
      const dstVal = data.destination.counts[type]
      const isTemplate = srcVal && typeof srcVal === "object" && srcVal.template

      let keepingHtml, movingHtml
      if (isTemplate) {
        const dstTotal = (dstVal && typeof dstVal === "object") ? (dstVal.total ?? 0) : (dstVal ?? 0)
        keepingHtml = `<span>${dstTotal}</span>`
        const newCount = srcVal.new ?? 0
        const matched = srcVal.matched ?? 0
        const subtitle = matched > 0
          ? `<span class="block text-[11px] text-text-muted whitespace-nowrap">${matched} matched</span>`
          : ""
        movingHtml = `<span class="font-medium whitespace-nowrap">${newCount} new</span>${subtitle}`
      } else {
        keepingHtml = `<span>${dstVal ?? 0}</span>`
        movingHtml = `<span class="font-medium">${srcVal ?? 0}</span>`
      }

      const row = document.createElement("div")
      row.className = "flex items-center px-3 py-2 text-[13px] gap-3"
      row.innerHTML = `
        <div class="w-6 shrink-0"><input type="checkbox" id="${checkboxId}" data-module="${moduleKey}" checked class="form-checkbox h-4 w-4 text-brand"></div>
        <label for="${checkboxId}" class="flex-1 min-w-0 text-text-heading cursor-pointer truncate">${this._escape(type)}</label>
        <div class="w-20 text-right shrink-0 text-text-muted">${keepingHtml}</div>
        <div class="w-32 text-right shrink-0 text-text-heading">${movingHtml}</div>
      `
      this.comparisonBodyTarget.appendChild(row)
    })
  }

  _moduleKeyForType(type) {
    const t = type.toLowerCase()
    if (t.includes("daily") || t.includes("monthly") || t.includes("submittal")) return "reports"
    if (t.includes("action")) return "action_items"
    if (t.includes("photo")) return "photos"
    if (t.includes("document")) return "documents"
    return "finances"
  }

  _selectedModules() {
    const checkboxes = this.comparisonBodyTarget.querySelectorAll("input[type=checkbox][data-module]")
    const selected = new Set()
    checkboxes.forEach(cb => { if (cb.checked) selected.add(cb.dataset.module) })
    return Array.from(selected)
  }

  // ── Step 3: confirmation ───────────────────────────────────────────────

  validateConfirm() {
    const typed = this.typedNameTarget.value.trim()
    const password = this.passwordTarget.value
    const expected = this._selectedSource ? this._selectedSource.name : ""
    const ok = typed === expected && password.length > 0
    this.confirmBtnTarget.disabled = !ok
  }

  submitMerge() {
    const password = this.passwordTarget.value
    const typed = this.typedNameTarget.value.trim()
    const modules = this._selectedModules()
    if (modules.length === 0) {
      this._showConfirmError("Select at least one module to merge")
      return
    }

    const fd = new FormData()
    fd.append("source_project_id", this._selectedSource.id)
    fd.append("typed_name", typed)
    fd.append("password", password)
    modules.forEach(m => fd.append("modules[]", m))

    this.confirmBtnTarget.disabled = true

    fetch(`/projects/${this.projectIdValue}/merges`, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfTokenValue, "Accept": "application/json" },
      credentials: "same-origin",
      body: fd
    }).then(async r => {
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        this.confirmBtnTarget.disabled = false
        this._showConfirmError(data.error || "Merge failed to start")
        return
      }
      this._closeModal("merge-duplicate")
      this._showToast(`Merge started…`)
      this._subscribeProgress(data.merge_id)
    }).catch(() => {
      this.confirmBtnTarget.disabled = false
      this._showConfirmError("Network error — try again")
    })
  }

  // ── Revert ─────────────────────────────────────────────────────────────

  submitRevert() {
    const mergeId = this.revertMergeIdTarget.value
    const password = this.revertPasswordTarget.value
    if (!mergeId || !password) {
      this._showRevertError("Enter your password")
      return
    }
    const fd = new FormData()
    fd.append("password", password)

    fetch(`/projects/${this.projectIdValue}/merges/${mergeId}/revert`, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfTokenValue, "Accept": "application/json" },
      credentials: "same-origin",
      body: fd
    }).then(async r => {
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        this._showRevertError(data.error || "Revert failed to start")
        return
      }
      this._closeModal("revert-merge")
      this._showToast("Revert started…")
      this._subscribeProgress(data.merge_id)
    }).catch(() => this._showRevertError("Network error — try again"))
  }

  // ── ActionCable progress ──────────────────────────────────────────────

  _subscribeProgress(mergeId) {
    if (!this._consumer) this._consumer = createConsumer()
    if (this._subscription) this._subscription.unsubscribe()
    if (this._progressToast) this._progressToast.remove()

    this._progressToast = this._buildProgressToast()
    document.body.appendChild(this._progressToast)

    this._subscription = this._consumer.subscriptions.create(
      { channel: "ProjectMergeChannel", merge_id: mergeId },
      {
        received: (data) => this._onProgress(data)
      }
    )
  }

  _buildProgressToast() {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.style.minWidth = "320px"
    el.innerHTML = `
      <div class="flex flex-col gap-1 w-full">
        <div class="flex items-center justify-between gap-2">
          <span class="font-medium" data-merge-progress-label>Merge starting…</span>
          <span class="text-[12px] text-gray-500" data-merge-progress-pct>0%</span>
        </div>
        <div class="w-full h-1.5 bg-gray-200 rounded overflow-hidden">
          <div class="h-full bg-brand transition-all" style="width: 0%" data-merge-progress-bar></div>
        </div>
      </div>
    `
    return el
  }

  _onProgress(data) {
    if (!this._progressToast) return
    const pct = Math.max(0, Math.min(100, data.progress || 0))
    const bar = this._progressToast.querySelector("[data-merge-progress-bar]")
    const pctLabel = this._progressToast.querySelector("[data-merge-progress-pct]")
    const label = this._progressToast.querySelector("[data-merge-progress-label]")
    if (bar) bar.style.width = `${pct}%`
    if (pctLabel) pctLabel.textContent = `${pct}%`
    if (label) label.textContent = `${data.current_module || ""} — ${data.message || ""}`

    if (data.progress === 100) {
      setTimeout(() => { window.location.reload() }, 800)
    } else if (data.progress < 0) {
      this._progressToast.classList.remove("flash-toast-notice")
      this._progressToast.classList.add("flash-toast-alert")
      if (label) label.textContent = `Error: ${data.message || "merge failed"}`
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  _resetWizard() {
    this._currentStep = 1
    this._selectedSource = null
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasSearchResultsTarget) this.searchResultsTarget.innerHTML = ""
    if (this.hasSearchEmptyTarget) this.searchEmptyTarget.classList.add("hidden")
    if (this.hasComparisonBodyTarget) this.comparisonBodyTarget.innerHTML = ""
    if (this.hasTypedNameTarget) this.typedNameTarget.value = ""
    if (this.hasPasswordTarget) this.passwordTarget.value = ""
    if (this.hasConfirmErrorTarget) this.confirmErrorTarget.classList.add("hidden")
    this._goTo(1)
  }

  _goTo(step) {
    this._currentStep = step
    if (this.hasStep1Target) this.step1Target.classList.toggle("hidden", step !== 1)
    if (this.hasStep2Target) this.step2Target.classList.toggle("hidden", step !== 2)
    if (this.hasStep3Target) this.step3Target.classList.toggle("hidden", step !== 3)

    if (this.hasStepLabel1Target) this._setStepLabel(this.stepLabel1Target, step === 1)
    if (this.hasStepLabel2Target) this._setStepLabel(this.stepLabel2Target, step === 2)
    if (this.hasStepLabel3Target) this._setStepLabel(this.stepLabel3Target, step === 3)

    if (this.hasBackBtnTarget) this.backBtnTarget.classList.toggle("hidden", step === 1)
    if (this.hasContinueBtnTarget) this.continueBtnTarget.classList.toggle("hidden", step === 3)
    if (this.hasConfirmBtnTarget) this.confirmBtnTarget.classList.toggle("hidden", step !== 3)

    if (this.hasContinueBtnTarget) {
      this.continueBtnTarget.disabled = step === 1 ? !this._selectedSource : false
    }
    if (this.hasConfirmBtnTarget && step === 3) {
      this.validateConfirm()
    }
  }

  _setStepLabel(el, active) {
    el.classList.toggle("font-medium", active)
    el.classList.toggle("text-brand", active)
    el.classList.toggle("text-text-muted", !active)
  }

  _openModal(id) {
    const modal = document.querySelector(`[data-modal-id-value="${id}"]`)
    if (!modal) return
    const ctrl = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (ctrl && typeof ctrl.open === "function") ctrl.open()
  }

  _closeModal(id) {
    const modal = document.querySelector(`[data-modal-id-value="${id}"]`)
    if (!modal) return
    const ctrl = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (ctrl && typeof ctrl.close === "function") ctrl.close()
  }

  _showConfirmError(msg) {
    if (!this.hasConfirmErrorTarget) return
    this.confirmErrorTarget.textContent = msg
    this.confirmErrorTarget.classList.remove("hidden")
  }

  _showRevertError(msg) {
    if (!this.hasRevertErrorTarget) return
    this.revertErrorTarget.textContent = msg
    this.revertErrorTarget.classList.remove("hidden")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.innerHTML = `<span>${this._escape(message)}</span>`
    document.body.appendChild(el)
  }

  _escape(s) {
    if (s == null) return ""
    return String(s).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
